import React, { useState, useCallback, useMemo } from 'react'
import { Stack, FormControl, Select, MenuItem, Box } from '@mui/material'
import BoardList from '@components/Board/BoardList'
import BoardListCard from '@components/Board/BoardListCard'
import BasicModal from '@components/Modal/BasicModal'
import { useIsMobile } from '@/hooks/Responsive'
import { fetchMyContentsConcernList, deleteMyContentsConcern } from '@/api/mypage'
import AlertConfirmModal from '@/components/Modal/AlertConfirmModal'
import { useConfirm } from '@/components/ConfirmProvider'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@/data/nav'
import { useAuth } from '@/components/AuthContext'

export function XRToolbar({ isMobile, xr, onXRChange }) {
    return (
        <Stack
            direction={isMobile ? 'column' : 'row'}
            alignItems="center"
            spacing={1}
            sx={{ flex: 'auto', width: isMobile ? '100%' : 'auto' }}
        >
            <FormControl sx={{ flex: 'auto', width: isMobile ? '100%' : '220px' }}>
                <Select
                    name="xr"
                    value={xr}
                    onChange={(e) => onXRChange?.(e.target.value)}
                    displayEmpty
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                width: '100%',
                                maxWidth: (theme) =>
                                    isMobile ? `calc(100vw - ${theme.spacing(8)})` : '220px',
                            },
                        },
                    }}
                    sx={{
                        width: '100%',
                        maxWidth: (theme) =>
                            isMobile ? `calc(100vw - ${theme.spacing(8)})` : '220px',
                    }}
                >
                    <MenuItem value="전체">전체</MenuItem>
                    <MenuItem value="체험형XR">체험형XR</MenuItem>
                    <MenuItem value="실습형XR">실습형XR</MenuItem>
                </Select>
            </FormControl>
        </Stack>
    )
}

export default function MyInterested() {
    const { isMobile } = useIsMobile()
    const lang = useLang()
    const [openModal, setOpenModal] = useState(false)
    const [selected, setSelected] = useState(null)

    const [xrFilter, setXRFilter] = useState('전체')
    const [confirmedXR, setConfirmedXR] = useState('전체')
    const [confirmedSearch, setConfirmedSearch] = useState('')
    const [data, setData] = useState(null)

    const [confirmOpen, setConfirmOpen] = useState(false)
    const [itemToToggle, setItemToToggle] = useState(null);

    const [modalDisplayItem, setModalDisplayItem] = useState(null);

    const { user, loading, isAuthReady } = useAuth();
    const { alert } = useConfirm();

    const fetcher = useCallback(
        async ({ page = 1, pageSize = 6, keyword = '' }) => {
            if (data && data.page === page && data.pageSize === pageSize && data.keyword === confirmedSearch) {
                return { items: data.items, total: data.total };
            }

            const params = {
                page,
                pageSize,
                keyword: confirmedSearch,
                xrType: confirmedXR === '전체' ? '' : confirmedXR,
                accountId: user.accountId
            }
            try {
                const res = await fetchMyContentsConcernList({ params });
                const items = res.data.content.map(item => ({
                    id: item.cmsContentsId,
                    title: item.cmsContentsName,
                    xr: item?.contentType,
                    preview: !!item.previewUrl,
                    interest: true,
                    institution: false,
                    category: item.lcmsTaxonomyFullpath,
                    previewUrl: item.previewUrl,
                    thumbUrl: item?.thumbnailImgUrl, //|| imgArr[Math.floor(Math.random() * imgArr.length)].thumbUrl,
                    href: buildLangHref(`/contents/xrview/${item.cmsContentsId}`, lang),
                }));
                const total = res.data.totalElements;
                setData({ items, total, page, pageSize, keyword: confirmedSearch });
                return { items, total };
            } catch (error) {
                console.error('Error fetching my contents concern list:', error);
                setData(null);
                return { items: [], total: 0 };
            }
        },
        [confirmedXR,confirmedSearch, data]
    )

    const handlePreviewClick = useCallback((row) => {
        setSelected(row)
        setOpenModal(true)
    }, [])

    const toggleInterest = useCallback((row) => {
        setItemToToggle(row);
        setModalDisplayItem(row);
        setConfirmOpen(true);
    }, [])

    const handleConfirmToggle = useCallback(async () => {
        if (!itemToToggle) return;

        try {
            const params = {
                cmsContentsId: itemToToggle.id,
                accountId: user.accountId
            };
            await deleteMyContentsConcern(params);
            setData(null); // Invalidate cache to trigger re-fetch
        } catch (error) {
            console.error('Failed to delete interested content', error);
            // Here we could show an error alert to the user.
        } finally {
            alert("관심콘텐츠 목록에서 삭제되었습니다.");
            setConfirmOpen(false);
            setItemToToggle(null);
        }
    }, [itemToToggle]);

    return (
        <>
            <BoardList
                view="card"
                columns={[]}
                fetcher={fetcher}
                initialPage={1}
                initialPageSize={6}
                initialSort={{ field: 'id', direction: 'desc' }}
                CardComponent={BoardListCard}
                CardProps={{
                    gridCols: 3,
                    onPreviewClick: handlePreviewClick,
                    onInterestClick: toggleInterest,
                }}
                toolbarLeft={
                    <XRToolbar
                        isMobile={isMobile}
                        xr={xrFilter}
                        onXRChange={setXRFilter}
                    />
                }
                onSearchSubmit={(searchValue) => {
                    setConfirmedXR(xrFilter)
                    setConfirmedSearch(searchValue)
                }}
                variant="content"
                emptyMessage='나의 관심콘텐츠가 없습니다.'
            />
            <AlertConfirmModal
                open={confirmOpen}
                title="관심콘텐츠 삭제"
                description={`${modalDisplayItem?.title}\n해당 관심콘텐츠를 삭제하시겠습니까?`}
                type="confirm"
                onConfirm={handleConfirmToggle}
                onClose={() => setConfirmOpen(false)}
                onCancel={() => {
                    setConfirmOpen(false)
                    setItemToToggle(null)
                }}
            />
            <BasicModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                title={selected?.title}
                disableEnforceFocus
                disablePortal
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        paddingTop: '56.25%',
                        borderRadius: 2,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        component="iframe"
                        src={selected?.previewUrl || null}
                        title="YouTube video player"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 0,
                        }}
                    />
                </Box>
            </BasicModal>
        </>
    )
}
